"use client";

import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLang } from "@/lib/LanguageContext";
import CategoryShowcase from "@/components/pages/services-showcase/CategoryShowcase";
import AllServicesIndex from "@/components/pages/services-showcase/AllServicesIndex";
import { CATEGORY_ORDER, SHOWCASE, type ShowcaseCategory } from "@/components/pages/services-showcase/items";

type FilterCategory = "all" | "marketing" | "creative" | "web";

/** Waits for the tab switch to render (AnimatePresence exits first), then scrolls. */
function scrollWhenReady(getEl: () => HTMLElement | null) {
  // Let React commit the new tab before looking — the old tab may share ids (e.g. svc-seo)
  setTimeout(() => {
    const start = performance.now();
    const tick = () => {
      const el = getEl();
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      else if (performance.now() - start < 2000) requestAnimationFrame(tick);
    };
    tick();
  }, 50);
}

const CATEGORIES = ["marketing", "creative", "web"];

/** Keeps the open tab in the URL (?category=web) so it can be linked to */
function syncUrl(category: FilterCategory) {
  const url = new URL(window.location.href);
  if (category === "all") url.searchParams.delete("category");
  else url.searchParams.set("category", category);
  url.hash = "";
  history.replaceState(null, "", url);
}

export default function ServicesPage() {
  const { t } = useLang();
  const [activeFilter, setActiveFilter] = useState<FilterCategory>("all");
  const showFilter = (category: FilterCategory) => {
    setActiveFilter(category);
    syncUrl(category);
  };
  const sectionRef = useRef<HTMLElement>(null);
  // The pills only show once the visitor has started scrolling down the services
  const [docked, setDocked] = useState(false);

  useEffect(() => {
    const onScroll = () =>
      setDocked((sectionRef.current?.getBoundingClientRect().top ?? 1) < 0);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    // A target is either a category ("web") or a service id ("video-editing").
    // Service ids open their category tab and scroll to that service's section.
    const goTo = (target: string) => {
      if (CATEGORIES.includes(target)) {
        setActiveFilter(target as FilterCategory);
        syncUrl(target as FilterCategory);
        scrollWhenReady(() => sectionRef.current);
        return true;
      }
      for (const cat of CATEGORY_ORDER) {
        const item = SHOWCASE[cat].find((i) => i.serviceId === target);
        if (item) {
          setActiveFilter(cat);
          syncUrl(cat);
          scrollWhenReady(() => document.getElementById(`svc-${item.key}`));
          return true;
        }
      }
      return false;
    };

    // Initial load: ?category=web, or a #category / #service-id hash (which
    // goTo rewrites to ?category=)
    const param = new URLSearchParams(window.location.search).get("category");
    const hash = window.location.hash.replace("#", "");
    const linked = (param && goTo(param)) || (hash && goTo(hash));
    if (!linked && window.matchMedia("(max-width: 639px)").matches) {
      // No "All services" tab on phones — open on the first category
      setActiveFilter("web");
    }

    // Handle subsequent clicks from the header while already on /services
    const onCategorySelect = (e: Event) => goTo((e as CustomEvent<string>).detail);
    window.addEventListener("services:category", onCategorySelect);
    return () => window.removeEventListener("services:category", onCategorySelect);
  }, []);

  const tabs: { id: FilterCategory; label: string; short: string }[] = [
    { id: "all", label: t.services.allServices, short: t.services.allServices },
    ...[...t.categoriesList]
      .sort(
        (a, b) =>
          CATEGORY_ORDER.indexOf(a.id as ShowcaseCategory) - CATEGORY_ORDER.indexOf(b.id as ShowcaseCategory),
      )
      .map((c) => ({
        id: c.id as FilterCategory,
        label: c.label,
        short: c.short,
      })),
  ];

  const selectCategory = (category: FilterCategory) => {
    showFilter(category);
    // Scrolled down past the pill's spot? Bring the new tab back to its start
    if ((sectionRef.current?.getBoundingClientRect().top ?? 0) < 0) {
      scrollWhenReady(() => sectionRef.current);
    }
  };

  return (
    <section ref={sectionRef} className="section-padding">
      <div className="container">
        {/* Tab filter — docks top-center (below the header when it's showing),
           fading in only once the visitor scrolls into the services; takes no
           space from sm up. Horizontally scrollable on narrow screens */}
        <div
          className={`sticky top-3 [html[data-header=shown]_&]:top-[76px] lg:[html[data-header=shown]_&]:top-[122px] transition-[top,opacity,transform] duration-[400ms] ease-[cubic-bezier(0.4,0,0.2,1)] z-30 pointer-events-none mb-14 sm:mb-0 sm:h-0 sm:overflow-visible flex justify-start [justify-content:safe_center] overflow-x-auto scrollbar-hide ${
            docked ? "opacity-100 translate-y-0" : "sm:opacity-0 sm:-translate-y-3 sm:invisible"
          }`}
        >
          <div
            className="pointer-events-auto sm:h-max relative inline-flex flex-nowrap sm:flex-wrap items-center justify-center gap-1 p-1.5 rounded-full flex-shrink-0"
            style={{
              background: "rgba(20,20,27,0.82)",
              backdropFilter: "blur(16px) saturate(160%)",
              WebkitBackdropFilter: "blur(16px) saturate(160%)",
              border: "1px solid rgba(255,255,255,0.08)",
              boxShadow: "0 12px 40px -12px rgba(0,0,0,0.6)",
            }}
          >
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => selectCategory(tab.id)}
                className={`relative z-10 py-2 px-3 sm:px-4 md:px-5 rounded-full text-xs sm:text-sm font-medium transition-colors duration-200 cursor-pointer whitespace-nowrap ${
                  tab.id === "all" ? "hidden sm:block" : ""
                }`}
                style={{
                  color:
                    activeFilter === tab.id ? "#fff" : "rgba(255,255,255,0.45)",
                }}
              >
                {activeFilter === tab.id && (
                  <motion.span
                    layoutId="services-pill"
                    className="absolute inset-0 rounded-full z-[-1]"
                    style={{
                      background: "linear-gradient(135deg, #FFB76C, #FF419D)",
                    }}
                    transition={{ type: "spring", stiffness: 420, damping: 36 }}
                  />
                )}
                <span className="sm:hidden">{tab.short}</span>
                <span className="hidden sm:inline">{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeFilter}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.35 }}
          >
            {activeFilter === "all" ? (
              <AllServicesIndex onSelectCategory={selectCategory} />
            ) : (
              <CategoryShowcase category={activeFilter} />
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
}

